import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'env/environment';
import { IEntity } from 'bookraw/shared/models/base-entity';
import { ListResponse } from 'bookraw/shared/models/api/list.response';
import { ItemResponse } from 'bookraw/shared/models/api/item.response';
import { PaginationData } from 'bookraw/shared/models/pagination-data';
import { QueryParams } from 'bookraw/shared/models/query-params';
import { tap } from 'rxjs/operators';
import { random } from "micro-dash";

@Injectable()
export abstract class ListService<TModel extends IEntity>  {
  public abstract url: string;

  queryParams: QueryParams;
  paginationData: PaginationData<TModel>;
  data: TModel[] = null;

  constructor(public http: HttpClient) {
    this.queryParams = new QueryParams();
    this.paginationData = new PaginationData<TModel>();
  }

  getList(params?: {[key: string]: string}): Observable<ListResponse<TModel>> {
    // A lapozható listákhoz a get()-et használjuk, azonban a szűrők elküldéséhez szükség van a queryParams elküldésére,
    // a limitet és offsetet azonban itt resetelnünk kell, hogy ne ragadjon be előzőleg beállíottból
    this.queryParams.setQueryParam('limit', null );
    this.queryParams.setQueryParam('offset', null);
    if (params) {
      for (const paramsKey of Object.keys(params)) {
        this.queryParams.setQueryParam(paramsKey, params[paramsKey]);
      }
    }

    return this.http.get<ListResponse<TModel>>(environment.baseURL + this.url + this.queryParams.paramString);
  }

  get(): Observable<PaginationData<TModel>> {
    this.queryParams.setQueryParam('limit', '' + this.paginationData.limit);
    this.queryParams.setQueryParam('offset', '' + (this.paginationData.offset * this.paginationData.limit));
    return this.http.get<PaginationData<TModel>>(environment.baseURL + this.url + this.queryParams.paramString).pipe(tap(
      (data) => {
        this.paginationData.savePaginationData(data);
        this.data = (data as any).result.list;
      }
    ));
  }

  getById(id: number, language?: string): Observable<ItemResponse<TModel>> {
    if (language) {
      const locale = new HttpParams().set('entityLocale', language);
      const headers = new HttpHeaders()
        .append('Accept-Language', 'en')
        .append('Accept-Language', 'es')
        .append('Accept-Language', 'id')
        .append('Accept-Language', 'fr')
        .append('Accept-Language', 'de')
        .append('Accept-Language', 'kr')
        .append('Accept-Language', 'pt')
        .append('Accept-Language', 'cn')
        .append('Accept-Language', 'my')
        .append('Accept-Language', 'ru')
        .append('Accept-Language', 'kk')
        .append('Accept-Language', 'vn')
        .append('Accept-Language', 'jp');

      return this.http.get<ItemResponse<TModel>>(environment.baseURL + this.url + '/' + id + this.queryParams.paramString, {
        params: locale,
        headers: headers
      });
    }
    return this.http.get<ItemResponse<TModel>>(environment.baseURL + this.url + '/' + id + this.queryParams.paramString);
  }

  create(data): Observable<ItemResponse<TModel>> {
    return this.http.post<ItemResponse<TModel>>(environment.baseURL + this.url + this.queryParams.paramString, data);
  }

  update(data: TModel, language?: string): Observable<ItemResponse<TModel>> {
    if (language) {
      const locale = new HttpParams().set('entityLocale', language);

      return this.http.put<ItemResponse<TModel>>(environment.baseURL + this.url + '/' + data.id + this.queryParams.paramString, data, {
        params: locale
      });
    }
    return this.http.put<ItemResponse<TModel>>(environment.baseURL + this.url + '/' + data.id + this.queryParams.paramString, data);
  }

  delete(id: number, withQueryParams: boolean = false): Observable<any> {
    return this.http.delete(environment.baseURL + this.url + '/' + id + (withQueryParams ? this.queryParams.paramString : '') );
  }

  setQueryParam = (key: string, value: string | null | undefined): void => {
    this.queryParams.setQueryParam(key, value);
  }

  resetQueryParams() {
    this.queryParams.resetQueryParams();
  }

  /**
   * Beállít egy új QueryParams objektumot, majd visszaadja a régit.
   * @param params
   */
  setAllQueryParams(params: QueryParams) {
    const originalParams = this.queryParams;
    this.queryParams = params;
    return originalParams;
  }

  /**
   * Statikus adatok lekérése, a daily reading time teszteléséhez
   */
  getStaticList(selectedMonth): Observable<ListResponse<any>> {
    return new Observable((observer) => {
        const cycleArray = [];
        for (let day = 1; day <= 31; day++) {
          const randomReadTime = random(0, 20);
          const dayString = day < 10 ? `0${day}` : `${day}`;

          cycleArray.push({
            day: `${selectedMonth}-${dayString}`,
            streak: random(0, 3),
            readTimeGoal: 20.0,
            readTime: randomReadTime,
          });
        }

        setTimeout(() => {
          observer.next({
            result: {
              total: 10,
              list: cycleArray
            }
          });

          observer.complete();
        }, 1000);
    });
  }

}
